import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Drawer,
    List,
    ListItem,
    ListItemText,
    Typography,
    Divider,
    TextField,
    Button,
    Box,
    IconButton,
    List as MUIList,
    ListItemButton,
    ListItemIcon,
    ListItemText as MUIListItemText,
    Radio,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const CartDrawer = ({
                        open,
                        onClose,
                        cartItems,
                        onSubmit,
                        onIncrement,
                        onDecrement,
                        // yangi propslar
                        buttonColor,
                        buttonTextColor,
                        priceColor,
                        showPrice = true,
                        showClickPay = false,
                        showOfflinePay = true,
                    }) => {
    const [note, setNote] = React.useState('');
    const [deliveryTime, setDeliveryTime] = React.useState('');
    const { t } = useTranslation();

    const [paymentMethod, setPaymentMethod] = React.useState(
        showOfflinePay ? 'OFFLINE' : (showClickPay ? 'CLICK' : 'OFFLINE')
    );

    const totalSum = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    const handleOrderSubmit = () => {
        onSubmit({ note, deliveryTime, paymentMethod });
        // CLICK’da redirect bo‘ladi — shu paytda oynani darhol yopmaslik qulay.
        if (paymentMethod !== 'CLICK') onClose();
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box width={320} padding={2}>
                <Typography variant="h6">{t('cartTitle') || 'Savatcha'}</Typography>
                <Divider sx={{ my: 2 }} />

                <List>
                    {cartItems.map((item) => (
                        <ListItem
                            key={item.id}
                            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}
                        >
                            <ListItemText
                                primary={item.name}
                                secondary={
                                    showPrice
                                        ? `${t('price') || 'Narx'}: ${item.price.toLocaleString()} so'm x ${item.quantity} = ${(item.price * item.quantity).toLocaleString()} so'm`
                                        : undefined
                                }
                                secondaryTypographyProps={{
                                    sx: priceColor ? { color: priceColor, fontWeight: 600 } : { fontWeight: 600 },
                                }}
                            />

                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <IconButton onClick={() => onDecrement(item.id)} size="small">
                                    <RemoveIcon />
                                </IconButton>
                                <Typography variant="body1" sx={{ mx: 1 }}>
                                    {item.quantity}
                                </Typography>
                                <IconButton onClick={() => onIncrement(item.id)} size="small">
                                    <AddIcon />
                                </IconButton>
                            </Box>
                        </ListItem>
                    ))}
                </List>

                {showPrice && (
                    <Typography
                        variant="h6"
                        sx={{ mt: 2, fontWeight: 'bold', ...(priceColor ? { color: priceColor } : {}) }}
                    >
                        {t('total') || 'Jami'}: {totalSum.toLocaleString()} so'm
                    </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <TextField
                    label={t('note') || 'Izoh'}
                    fullWidth
                    multiline
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />

                <TextField
                    label={t('deliveryTime') || 'Yetkazib berish vaqti'}
                    fullWidth
                    type="datetime-local"
                    sx={{ mt: 2 }}
                    value={deliveryTime}
                    onChange={(e) => setDeliveryTime(e.target.value)}
                />

                {(showClickPay || showOfflinePay) && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t('paymentMethods') || "To'lov usullari"}
                        </Typography>

                        <MUIList sx={{ p: 0 }}>
                            {showOfflinePay && (
                                <ListItemButton
                                    onClick={() => setPaymentMethod('OFFLINE')}
                                    selected={paymentMethod === 'OFFLINE'}
                                    sx={{
                                        mb: 1,
                                        border: '1px solid',
                                        borderRadius: 1.5,
                                        borderColor: paymentMethod === 'OFFLINE' ? 'primary.main' : 'divider',
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        {/* public/icons/cash.png faylini public papkaga qo‘ying */}
                                        <Box
                                            component="img"
                                            src="/icons/cash.png"
                                            alt="Naqd to'lov"
                                            sx={{ width: 28, height: 28, objectFit: 'contain' }}
                                        />
                                    </ListItemIcon>

                                    <MUIListItemText
                                        primary={t('payOfflineLabel') || "1. Naqd to'lov"}
                                        secondary={t('payOfflineDesc') || "Kuryerga yoki kassaga to'lov"}
                                        primaryTypographyProps={{ fontWeight: paymentMethod === 'OFFLINE' ? 700 : 500 }}
                                    />

                                    <Radio edge="end" checked={paymentMethod === 'OFFLINE'} tabIndex={-1} size="small" />
                                </ListItemButton>
                            )}

                            {showClickPay && (
                                <ListItemButton
                                    onClick={() => setPaymentMethod('CLICK')}
                                    selected={paymentMethod === 'CLICK'}
                                    sx={{
                                        border: '1px solid',
                                        borderRadius: 1.5,
                                        borderColor: paymentMethod === 'CLICK' ? 'primary.main' : 'divider',
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        {/* public/icons/click.png faylini public papkaga qo‘ying */}
                                        <Box
                                            component="img"
                                            src="/icons/click.png"
                                            alt="CLICK orqali to'lov"
                                            sx={{ width: 28, height: 28, objectFit: 'contain' }}
                                        />
                                    </ListItemIcon>

                                    <MUIListItemText
                                        primary={t('payWithClickLabel') || "2. Click orqali to'lov"}
                                        secondary={t('payWithClickDesc') || "Onlayn to'lov (CLICK)"}
                                        primaryTypographyProps={{ fontWeight: paymentMethod === 'CLICK' ? 700 : 500 }}
                                    />

                                    <Radio edge="end" checked={paymentMethod === 'CLICK'} tabIndex={-1} size="small" />
                                </ListItemButton>
                            )}
                        </MUIList>
                    </Box>
                )}

                <Button
                    variant="contained"
                    fullWidth
                    sx={{
                        mt: 2,
                        backgroundColor: buttonColor || 'primary.main',
                        color: buttonTextColor || '#fff',
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: buttonColor || 'primary.dark',
                            opacity: 0.9,
                        },
                    }}
                    onClick={handleOrderSubmit}
                >
                    {t('submitOrder') || 'Buyurtma berish'}
                </Button>
            </Box>
        </Drawer>
    );
};

export default CartDrawer;
